/**
 * Cadastre360™ Phase XII — Enterprise Information Architecture.
 * Presentation-only reorganisation into 12 permanent sovereign governance
 * domains + 6 Future Ready reserved placeholders. Every entry still maps to
 * an EXISTING route — zero regression, zero duplication.
 */
import {
  LayoutDashboard, Landmark, Pickaxe, Compass, ScrollText, FileText,
  ShieldCheck, ClipboardCheck, Gavel, Globe2, Sparkles, BookOpen,
  Banknote, Bell, Award, ShieldAlert, FileScan, PenLine, FileBarChart2,
  User, BarChart2, Newspaper, Plug, TrendingUp, Database, Command,
  Brain, Crown, Building2, Radar, Target, MapPin, Route as RouteIcon,
  Boxes, Star, Clock, Presentation, Zap, Leaf, Waves, TreePine, Wheat,
  Anchor, Cable, Bolt, Lock, Network,
} from "lucide-react";

// Semantic colour governance (Phase X).
const C_EXEC       = "#7B2FBE";
const C_PRESIDENTIAL = "#7B2FBE";
const C_LAND       = "#C99A2E";
const C_MINING     = "#C99A2E";
const C_SURVEY     = "#C99A2E";
const C_ENV        = "#2E7D6B";
const C_GIS        = "#4FA3D1";
const C_INTEL      = "#7B2FBE";
const C_KNOWLEDGE  = "#C99A2E";
const C_PUBLIC     = "#6BB88A";
const C_REPORT     = "#4FA3D1";
const C_ADMIN      = "#B3C2CC";
const C_RESERVED   = "#5A7A8A";

export const NAV_CATALOG = [
  // 1. EXECUTIVE GOVERNMENT
  {
    id: "executive-government", label: "Executive Government", accent: C_EXEC,
    items: [
      { to: "/app/national-executive-dashboard", label: "National Executive Dashboard™", icon: Crown,          testid: "nav-national-executive-dashboard", exec: true },
      { to: "/app/cabinet-execution",      label: "Cabinet Execution Engine™",         icon: Award,           testid: "nav-cabinet-execution",       exec: true },
      { to: "/app/sovereign-performance",  label: "Sovereign Performance Platform™",  icon: TrendingUp,      testid: "nav-sovereign-performance",   exec: true },
      { to: "/app/national-outcomes",      label: "National Outcomes Centre™",         icon: Star,            testid: "nav-national-outcomes",        exec: true },
      { to: "/app/strategic-decisions",    label: "Strategic Decision Centre™",       icon: Brain,           testid: "nav-strategic-decisions",     exec: true },
      { to: "/app/executive-intelligence", label: "Executive Decision Intelligence™",icon: Crown,           testid: "nav-executive-intelligence",   exec: true },
      { to: "/app/operating-model",        label: "Sovereign Operating Model™",       icon: Network,         testid: "nav-operating-model",          exec: true },
      { to: "/app/executive-canvas",       label: "Whole-of-Gov Executive Canvas™",   icon: LayoutDashboard, testid: "nav-executive-canvas",         exec: true },
      { to: "/app/cabinet-workspaces",     label: "Cabinet Portfolio Workspaces™",    icon: Building2,       testid: "nav-cabinet-workspaces",       exec: true },
      { to: "/app/province360",            label: "Province360™",                     icon: MapPin,          testid: "nav-province360",              exec: true },
      { to: "/app/sovereign-readiness",    label: "Sovereign Readiness™ (Phase XVI)", icon: ShieldCheck,     testid: "nav-sovereign-readiness",      exec: true },
      { to: "/app/executive",              label: "Executive Attention Room™",       icon: Radar,           testid: "nav-executive-attention",     exec: true },
      { to: "/app/gsros",                  label: "GSROS™",                          icon: Building2,       testid: "nav-gsros",                    exec: true },
      { to: "/app/thom360",                label: "THOM360 Architecture",             icon: Network,         testid: "nav-thom360",                  exec: true },
      { to: "/app/cockpit",                label: "Sovereign Platform Cockpit™",      icon: Building2,       testid: "nav-cockpit",                  exec: true },
      { to: "/app/value",                  label: "National Value Centre™",           icon: Award,           testid: "nav-value",                    exec: true },
      { to: "/app/transformation",         label: "Transformation Intelligence™",     icon: TrendingUp,      testid: "nav-transformation",           exec: true },
      { to: "/app/cross-pillar",           label: "Cross-Pillar Intelligence",       icon: Sparkles,        testid: "nav-cross-pillar",             exec: true },
      { to: "/app/briefings",              label: "Executive Briefings™",             icon: Newspaper,       testid: "nav-briefings",                exec: true },
      { to: "/app/performance",            label: "National Performance™",            icon: BarChart2,       testid: "nav-performance",              exec: true },
    ],
  },
  // 2. PRESIDENTIAL & CABINET
  {
    id: "presidential-cabinet", label: "Presidential & Cabinet", accent: C_PRESIDENTIAL,
    items: [
      { to: "/app/my-workspace",           label: "Role-Based Workspace™",           icon: Crown,           testid: "nav-my-workspace",             exec: true },
      { to: "/app",                        label: "National Situation Room™",         icon: LayoutDashboard, testid: "nav-dashboard",                exec: true, exact: true },
      { to: "/app/sovereign-intelligence", label: "Sovereign Intelligence Centre™",   icon: Crown,           testid: "nav-sovereign-intelligence",   exec: true },
      { to: "/app/watchlist",              label: "Executive Watchlist™",             icon: Star,            testid: "nav-watchlist",                exec: true },
      { to: "/app/timeline",               label: "Executive Timeline™",              icon: Clock,           testid: "nav-timeline",                 exec: true },
      { to: "/app/boardroom",              label: "Boardroom Presentation™",          icon: Presentation,    testid: "nav-boardroom",                exec: true },
    ],
  },
  // 3. LAND GOVERNANCE
  {
    id: "land-governance", label: "Land Governance™", accent: C_LAND,
    items: [
      { to: "/app/land",     label: "Parcel Administration™", icon: Landmark,   testid: "nav-land" },
      { to: "/app/registry", label: "Land Rights & Registry™",icon: ScrollText, testid: "nav-registry" },
      { to: "/app/history",  label: "Parcel Timeline™",       icon: RouteIcon,  testid: "nav-history" },
      { to: "/app/spatial",  label: "Spatial Validation™",    icon: PenLine,    testid: "nav-spatial" },
    ],
  },
  // 4. MINERAL GOVERNANCE
  {
    id: "mineral-governance", label: "Mineral Governance™", accent: C_MINING,
    items: [
      { to: "/app/mining",              label: "Mining Cadastre™",     icon: Pickaxe, testid: "nav-mining" },
      { to: "/app/mineral-governance",  label: "Licensing & Governance™", icon: Award, testid: "nav-mineral-governance" },
      { to: "/app/mineral-plus",        label: "Mineral+ Programme™",  icon: Target,  testid: "nav-mineral-plus" },
      { to: "/app/global-governance",   label: "Global Land Governance™", icon: Globe2, testid: "nav-global-governance" },
    ],
  },
  // 5. SURVEY & GEOSPATIAL GOVERNANCE
  {
    id: "survey-governance", label: "Survey & Geospatial Governance™", accent: C_SURVEY,
    items: [
      { to: "/app/survey", label: "Survey Administration™", icon: Compass, testid: "nav-survey" },
    ],
  },
  // 6. ENVIRONMENTAL GOVERNANCE
  {
    id: "environmental-governance", label: "Environmental Governance™", accent: C_ENV,
    items: [
      { to: "/app/environment", label: "Environmental Cadastre™", icon: Leaf, testid: "nav-environment" },
    ],
  },
  // 7. NATIONAL GIS & DIGITAL TWIN
  {
    id: "national-gis-twin", label: "National GIS & Digital Twin™", accent: C_GIS,
    items: [
      { to: "/app/twin",    label: "National Digital Twin™", icon: Boxes,   testid: "nav-twin" },
      { to: "/app/spatial", label: "Spatial Validation™",    icon: PenLine, testid: "nav-spatial-2" },
    ],
  },
  // 8. RESOURCE INTELLIGENCE
  {
    id: "resource-intelligence", label: "Resource Intelligence™", accent: C_INTEL,
    items: [
      { to: "/app/intelligence",           label: "Cadastre Intelligence™",   icon: Sparkles,  testid: "nav-intel" },
      { to: "/app/enterprise-intelligence",label: "Enterprise Intelligence™",  icon: Brain,     testid: "nav-enterprise-intelligence" },
      { to: "/app/analytics",              label: "Analytics Centre™",         icon: TrendingUp,testid: "nav-analytics" },
    ],
  },
  // 9. DATA & KNOWLEDGE
  {
    id: "data-knowledge", label: "Data & Knowledge™", accent: C_KNOWLEDGE,
    items: [
      { to: "/app/knowledge",                   label: "Knowledge360™",             icon: BookOpen, testid: "nav-knowledge" },
      { to: "/app/digitisation",                label: "Digitisation Centre™",      icon: FileScan, testid: "nav-digitisation" },
      { to: "/app/migration",                   label: "Migration Centre™",         icon: Database, testid: "nav-migration" },
      { to: "/app/migration/situation",         label: "Migration Situation Room™", icon: Command,  testid: "nav-migration-situation" },
      { to: "/app/mining-migration",            label: "Mining Data Migration™",    icon: Pickaxe,  testid: "nav-mining-migration" },
      { to: "/app/mining-migration/command",    label: "Migration Command™",        icon: Command,  testid: "nav-mining-migration-command" },
    ],
  },
  // 10. PUBLIC SERVICES
  {
    id: "public-services", label: "Public Services™", accent: C_PUBLIC,
    items: [
      { to: "/app/portal",                     label: "Applicant Portal™",  icon: User,   testid: "nav-portal" },
      { to: "/app/mining-migration/surveyors", label: "Surveyor Portal™",   icon: Compass,testid: "nav-mining-migration-surveyors" },
      { to: "/app/mining-migration/claimants", label: "Claimant Portal™",   icon: User,   testid: "nav-mining-migration-claimants" },
      { to: "/app/public-preview",             label: "Public Portal Preview™", icon: Globe2, testid: "nav-public" },
    ],
  },
  // 11. REPORTING & COMPLIANCE
  {
    id: "reporting-compliance", label: "Reporting & Compliance™", accent: C_REPORT,
    items: [
      { to: "/app/applications",       label: "Applications™",         icon: FileText,        testid: "nav-apps" },
      { to: "/app/compliance",         label: "Compliance™",           icon: ShieldCheck,     testid: "nav-compliance" },
      { to: "/app/inspections",        label: "Inspections™",          icon: ClipboardCheck,  testid: "nav-inspect" },
      { to: "/app/disputes",           label: "Disputes™",             icon: Gavel,           testid: "nav-disputes" },
      { to: "/app/revenue",            label: "Revenue Ledger™",       icon: Banknote,        testid: "nav-revenue" },
      { to: "/app/integrity",          label: "Integrity Centre™",     icon: ShieldAlert,     testid: "nav-integrity" },
      { to: "/app/reports",            label: "National Reports™",     icon: FileBarChart2,   testid: "nav-reports" },
      { to: "/app/compliance-report",  label: "Compliance Report™",    icon: Award,           testid: "nav-compliance-report" },
      { to: "/app/audit-register",     label: "Audit Register™",       icon: Award,           testid: "nav-audit-register" },
      { to: "/app/national-deployment",label: "National Deployment™",  icon: MapPin,          testid: "nav-national-deployment" },
    ],
  },
  // 12. ADMINISTRATION
  {
    id: "administration", label: "Administration™", accent: C_ADMIN, adminOnly: true,
    items: [
      { to: "/app/admin-console",       label: "Identity · Audit · Federation™", icon: Lock, testid: "nav-admin-console" },
      { to: "/app/integration",         label: "Integration Hub™",              icon: Plug,  testid: "nav-integration" },
      { to: "/app/notifications",       label: "Notification Centre™",           icon: Bell,  testid: "nav-notifications" },
      { to: "/app/capability-register", label: "Capability Register™",           icon: Award, testid: "nav-capability" },
    ],
  },
  // FUTURE READY DOMAINS — placeholders only, no implementation
  {
    id: "future-ready", label: "Future Ready", accent: C_RESERVED, reserved: true,
    items: [
      { to: "/app/thom360",  label: "Water Governance™ (Reserved)",         icon: Waves,   testid: "nav-reserved-water",       reserved: true },
      { to: "/app/thom360",  label: "Forestry Governance™ (Reserved)",      icon: TreePine,testid: "nav-reserved-forestry",   reserved: true },
      { to: "/app/thom360",  label: "Agriculture & Rangelands™ (Reserved)", icon: Wheat,   testid: "nav-reserved-agriculture",reserved: true },
      { to: "/app/thom360",  label: "Marine Governance™ (Reserved)",        icon: Anchor,  testid: "nav-reserved-marine",     reserved: true },
      { to: "/app/thom360",  label: "Energy Resources™ (Reserved)",         icon: Bolt,    testid: "nav-reserved-energy",     reserved: true },
      { to: "/app/thom360",  label: "Infrastructure Governance™ (Reserved)",icon: Cable,   testid: "nav-reserved-infra",      reserved: true },
    ],
  },
];

export const flattenNav = () => NAV_CATALOG.flatMap((d) => d.items);
